import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AlertTriangle, Bug, ChevronDown, RefreshCw, Trash2 } from "lucide-react";
import {
  listIssues,
  updateIssue,
  deleteIssue,
  type AdminIssue,
  type IssueStatus,
} from "@/lib/issues.functions";
import { requireAdmin } from "@/lib/admin-guard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/admin_/issues")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Issues & Bug Reports — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: IssuesPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-destructive">{error instanceof Error ? error.message : "Page unavailable"}</div>
  ),
});

const SOURCE_LABELS: Record<string, string> = {
  user_report: "🐛 Member report",
  client_error: "❌ Website error",
  server_error: "❌ Backend error",
  payment: "💳 Payment",
  email: "📧 Email",
  auth: "🔐 Login",
  audio: "🎧 Audio",
  ai: "🤖 AI reflection",
  upload: "📤 Upload",
  moderation: "⚠️ Moderation",
  other: "🚨 Other",
};

const STATUS_TABS: { value: IssueStatus | "all"; label: string }[] = [
  { value: "new", label: "New" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

function when(iso: string) {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Europe/London",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function IssuesPage() {
  const fetcher = useServerFn(listIssues);
  const updater = useServerFn(updateIssue);
  const remover = useServerFn(deleteIssue);
  const qc = useQueryClient();

  const [status, setStatus] = useState<IssueStatus | "all">("new");
  const [source, setSource] = useState<string>("all");

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin", "issues", status, source],
    queryFn: () => fetcher({ data: { status, source } }),
    refetchInterval: 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "issues"] });

  const setStatusMut = useMutation({
    mutationFn: (vars: { id: string; status: IssueStatus }) => updater({ data: vars }),
    onSuccess: () => {
      invalidate();
      toast.success("Updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const notesMut = useMutation({
    mutationFn: (vars: { id: string; admin_notes: string }) => updater({ data: vars }),
    onSuccess: () => {
      invalidate();
      toast.success("Note saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const issues = data?.issues ?? [];
  const counts = data?.counts;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-8">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 font-display text-2xl">
          <Bug className="h-6 w-6 text-[var(--gold-deep)]" aria-hidden /> Issues &amp; Bug Reports
        </h1>
        <p className="text-sm text-muted-foreground">
          Problems members have reported, and failures the platform detected itself. Alerts go to
          your inbox as they happen; repeats of the same problem are grouped here.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setStatus(t.value)}
            className={`rounded-full border px-4 py-1.5 text-sm ${
              status === t.value
                ? "border-[var(--gold-deep)] bg-[var(--gold-light)]/50 font-semibold"
                : "border-border text-muted-foreground hover:border-[var(--gold-deep)]/50"
            }`}
          >
            {t.label}
            {counts && t.value !== "all" ? ` (${counts[t.value] ?? 0})` : ""}
          </button>
        ))}

        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-sm"
        >
          <option value="all">All types</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <Button
          variant="outline"
          size="sm"
          className="ms-auto"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`me-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} aria-hidden />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : issues.length === 0 ? (
        <div className="rounded-2xl border border-[var(--gold-deep)]/20 bg-card p-10 text-center text-muted-foreground">
          Nothing here — no issues match this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              onStatus={(next) => setStatusMut.mutate({ id: issue.id, status: next })}
              onNotes={(notes) => notesMut.mutate({ id: issue.id, admin_notes: notes })}
              onDelete={() => {
                if (confirm("Delete this issue permanently?")) delMut.mutate(issue.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IssueCard({
  issue,
  onStatus,
  onNotes,
  onDelete,
}: {
  issue: AdminIssue;
  onStatus: (s: IssueStatus) => void;
  onNotes: (notes: string) => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(issue.admin_notes ?? "");

  return (
    <article
      className={`rounded-2xl border bg-card p-4 ${
        issue.status === "new" && issue.severity !== "warning"
          ? "border-destructive/40"
          : "border-[var(--gold-deep)]/20"
      }`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <Badge variant="outline">{SOURCE_LABELS[issue.source] ?? issue.source}</Badge>
        {issue.severity === "critical" && (
          <Badge variant="destructive">
            <AlertTriangle className="me-1 h-3 w-3" aria-hidden /> Critical
          </Badge>
        )}
        {issue.occurrences > 1 && <Badge variant="secondary">seen {issue.occurrences}×</Badge>}
        <span className="ms-auto text-xs text-muted-foreground">{when(issue.last_seen)}</span>
      </div>

      <h2 className="mt-2 break-words font-medium">{issue.title}</h2>

      <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
        {issue.page_path && (
          <div>
            <dt className="inline font-medium">Page: </dt>
            <dd className="inline break-all">{issue.page_path}</dd>
          </div>
        )}
        {issue.feature && (
          <div>
            <dt className="inline font-medium">Area: </dt>
            <dd className="inline">{issue.feature}</dd>
          </div>
        )}
        <div>
          <dt className="inline font-medium">Member: </dt>
          <dd className="inline break-all">{issue.user_email ?? "Not signed in / unknown"}</dd>
        </div>
        {issue.platform && (
          <div>
            <dt className="inline font-medium">Device: </dt>
            <dd className="inline">
              {[issue.platform, issue.app_version].filter(Boolean).join(" · ")}
            </dd>
          </div>
        )}
      </dl>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(["new", "investigating", "resolved"] as IssueStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => onStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs capitalize ${
              issue.status === s
                ? "border-[var(--gold-deep)] bg-[var(--gold-light)]/50 font-semibold"
                : "border-border text-muted-foreground hover:border-[var(--gold-deep)]/50"
            }`}
          >
            {s}
          </button>
        ))}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ms-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Details
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden />
        </button>
        <button
          onClick={onDelete}
          aria-label="Delete issue"
          className="rounded-full p-1.5 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
          {issue.detail && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {issue.source === "user_report" ? "What the member wrote" : "Error details"}
              </h3>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-3 text-xs">
                {issue.detail}
              </pre>
            </div>
          )}
          {issue.user_agent && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Browser / app
              </h3>
              <p className="break-all text-xs text-muted-foreground">{issue.user_agent}</p>
            </div>
          )}
          {issue.context && Object.keys(issue.context).length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Technical context
              </h3>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-3 text-xs">
                {JSON.stringify(issue.context, null, 2)}
              </pre>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            First seen {when(issue.first_seen)} · {issue.occurrences} occurrence
            {issue.occurrences === 1 ? "" : "s"}
          </p>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Your notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-background p-2 text-sm"
              placeholder="What you found, what you changed…"
            />
            <Button size="sm" variant="outline" onClick={() => onNotes(notes)}>
              Save note
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}
